import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { searchKeys } from '@/entities/search';
import { API_TAGS } from '@/shared/api';
import { createSample, deleteSample, fetchSample, fetchSamples, updateSample } from './api';
import type { SampleCreateRequest, SampleListParams, SampleUpdateRequest } from './types';

/**
 * Query keys for samples
 */
export const sampleKeys = {
  all: [API_TAGS.SAMPLES] as const,
  lists: () => [...sampleKeys.all, 'list'] as const,
  list: (params?: SampleListParams) => [...sampleKeys.lists(), { ...params }] as const,
  details: () => [...sampleKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...sampleKeys.details(), id] as const,
};

/**
 * Обесценивает выдачу списка диалогов после правки выборки.
 *
 * Список раздела выборок ограничен выборкой, поэтому её отбор и состав задают, какие диалоги
 * он показывает. Без этого сохранённая правка была бы видна только после перезапроса с нуля.
 */
const invalidateSampleDependentLists = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: searchKeys.all });
};

/**
 * Hook to fetch a list of samples
 */
export const useSamples = (params?: SampleListParams) => {
  return useQuery({
    queryKey: sampleKeys.list(params),
    queryFn: () => fetchSamples(params),
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to fetch samples with infinite scroll
 */
export const useInfiniteSamples = (params?: SampleListParams & { limit?: number; offset?: number }) => {
  const limit = params?.limit || 50;
  const initialPageParam = Math.floor((params?.offset || 0) / limit);

  const query = useInfiniteQuery({
    queryKey: sampleKeys.list(params),
    queryFn: ({ pageParam }) => fetchSamples({ ...params, limit, offset: pageParam * limit }),
    initialPageParam,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const totalPages = Math.ceil((lastPage.total || 0) / limit);
      const nextPage = lastPageParam + 1;
      return nextPage < totalPages ? nextPage : undefined;
    },
  });

  const data = useMemo(
    () => ({
      total: query.data?.pages[0]?.total,
      samples: query.data?.pages.flatMap((p) => p.samples),
    }),
    [query.data],
  );

  return {
    ...query,
    data,
  };
};

/**
 * Hook to fetch a single sample by ID
 */
export const useSample = (id?: number | string) => {
  return useQuery({
    // Ключ строится только по реальному идентификатору: без него запрос не уходит,
    // а общий ключ с `undefined` объединил бы в кэше все вызовы хука без выборки.
    queryKey: id ? sampleKeys.detail(id) : sampleKeys.details(),
    queryFn: () => fetchSample(id!),
    enabled: !!id && id !== 'create',
  });
};

/**
 * Hook to create a sample
 */
export const useCreateSample = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SampleCreateRequest) => createSample(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sampleKeys.lists() });
      invalidateSampleDependentLists(queryClient);
    },
  });
};

/**
 * Hook to update a sample
 */
export const useUpdateSample = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: SampleUpdateRequest }) => updateSample(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: sampleKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: sampleKeys.lists() });
      invalidateSampleDependentLists(queryClient);
    },
  });
};

/**
 * Hook to delete a sample
 */
export const useDeleteSample = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => deleteSample(id),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: sampleKeys.lists() });
      queryClient.removeQueries({ queryKey: sampleKeys.detail(deletedId) });
      invalidateSampleDependentLists(queryClient);
    },
  });
};
